'use client';
import React, { useState } from 'react';
import { FileText, Calculator, CheckCircle, AlertTriangle, Clock, Download, Send, Info } from 'lucide-react';

// ─── Official LRA Real Property Tax Rates ────────────────────────────────────
// Source: Liberia Revenue Authority – Real Property Tax Rate Schedule
// Ref: https://revenue.lra.gov.lr/real-property-taxes/

export const LRA_TAX_RATES = {
  developed: {
    commercial_building: { label: 'Commercial Building', rate: 0.015, rateDisplay: '1.5%', category: 'Developed Land' },
    industrial_building: { label: 'Industrial Building', rate: 0.015, rateDisplay: '1.5%', category: 'Developed Land' },
    residential_building: { label: 'Residential Building', rate: 0.0025, rateDisplay: '0.25%', category: 'Developed Land' },
    farm_urban: { label: 'Farm in Urban Areas', rate: 0.0033, rateDisplay: '0.33%', category: 'Developed Land' },
    farm_outside_urban: { label: 'Farm outside Urban Areas', rate: 0.0025, rateDisplay: '0.25%', category: 'Developed Land' },
    public_land_commercial: { label: 'Building on Public Land – Commercial', rate: 0.01, rateDisplay: '1%', category: 'Developed (Public Land)' },
    public_land_residential: { label: 'Building on Public Land – Residential', rate: 0.0014, rateDisplay: '0.14%', category: 'Developed (Public Land)' },
  },
  undeveloped: {
    city_town_lots: { label: 'City/Town Lots (within city/town limits)', rate: 0.035, rateDisplay: '3.5%', category: 'Undeveloped – Within City/Town' },
    farmland_undeveloped: { label: 'Farmland (within city/town limits)', rate: 0.04, rateDisplay: '4%', category: 'Undeveloped – Within City/Town' },
    vacant_acre_plus: { label: 'Acre or Above – Vacant Land (within city/town)', rate: 0.045, rateDisplay: '4.5%', category: 'Undeveloped – Within City/Town' },
    outside_city_all_land: { label: 'All Land without Building (outside city/town)', rate: null, rateDisplay: 'LRD $5.00/acre', category: 'Undeveloped – Outside City/Town', flatRate: 5, currency: 'LRD', unit: 'per acre' },
  },
};

interface TaxBill {
  billId: string;
  parcelId: string;
  address: string;
  county: string;
  owner: string;
  assessedValue: number;
  propertyTypeKey: string;
  propertyTypeLabel: string;
  taxRateDisplay: string;
  taxRate: number | null;
  totalTax: number;
  taxCurrency: string;
  dueDate: string;
  status: 'issued' | 'paid' | 'overdue' | 'partial';
  paidAmount: number;
  billDate: string;
  landCategory: 'developed' | 'undeveloped';
  areaAcres?: number;
}

const TAX_BILLS: TaxBill[] = [
  {
    billId: 'TB-2025-0042', parcelId: 'LR-MON-0042', address: '14 Broad St, Monrovia', county: 'Montserrado',
    owner: 'Kollie Trading Co.', assessedValue: 185000, propertyTypeKey: 'commercial_building',
    propertyTypeLabel: 'Commercial Building', taxRateDisplay: '1.5%', taxRate: 0.015,
    totalTax: 2775, taxCurrency: 'USD', dueDate: '2025-07-01', status: 'paid', paidAmount: 2775, billDate: '2025-01-15',
    landCategory: 'developed',
  },
  {
    billId: 'TB-2025-0117', parcelId: 'LR-MON-0117', address: '88 Tubman Blvd, Monrovia', county: 'Montserrado',
    owner: 'Mary Pewee', assessedValue: 92000, propertyTypeKey: 'residential_building',
    propertyTypeLabel: 'Residential Building', taxRateDisplay: '0.25%', taxRate: 0.0025,
    totalTax: 230, taxCurrency: 'USD', dueDate: '2025-07-01', status: 'overdue', paidAmount: 0, billDate: '2025-01-15',
    landCategory: 'developed',
  },
  {
    billId: 'TB-2025-0055', parcelId: 'LR-GBS-0055', address: 'Buchanan Port Zone', county: 'Grand Bassa',
    owner: 'LPA Holdings', assessedValue: 520000, propertyTypeKey: 'industrial_building',
    propertyTypeLabel: 'Industrial Building', taxRateDisplay: '1.5%', taxRate: 0.015,
    totalTax: 7800, taxCurrency: 'USD', dueDate: '2025-07-01', status: 'partial', paidAmount: 4000, billDate: '2025-01-15',
    landCategory: 'developed',
  },
  {
    billId: 'TB-2025-0031', parcelId: 'LR-BON-0031', address: 'Gbarnga Central Plot 3', county: 'Bong',
    owner: 'Bong County Dev. Corp.', assessedValue: 310000, propertyTypeKey: 'farm_urban',
    propertyTypeLabel: 'Farm in Urban Areas', taxRateDisplay: '0.33%', taxRate: 0.0033,
    totalTax: 1023, taxCurrency: 'USD', dueDate: '2025-07-01', status: 'paid', paidAmount: 1023, billDate: '2025-01-15',
    landCategory: 'developed',
  },
  {
    billId: 'TB-2025-0011', parcelId: 'LR-GGH-0011', address: 'Zwedru Central Market', county: 'Grand Gedeh',
    owner: 'Gedeh Commerce Ltd.', assessedValue: 145000, propertyTypeKey: 'commercial_building',
    propertyTypeLabel: 'Commercial Building', taxRateDisplay: '1.5%', taxRate: 0.015,
    totalTax: 2175, taxCurrency: 'USD', dueDate: '2025-07-01', status: 'issued', paidAmount: 0, billDate: '2025-02-01',
    landCategory: 'developed',
  },
  {
    billId: 'TB-2025-0004', parcelId: 'LR-MRY-0004', address: 'Harper Coastal Strip', county: 'Maryland',
    owner: 'Maryland Fisheries Ltd.', assessedValue: 0, propertyTypeKey: 'outside_city_all_land',
    propertyTypeLabel: 'All Land without Building (outside city/town)', taxRateDisplay: 'LRD $5.00/acre', taxRate: null,
    totalTax: 250, taxCurrency: 'LRD', dueDate: '2025-07-01', status: 'paid', paidAmount: 250, billDate: '2025-01-15',
    landCategory: 'undeveloped', areaAcres: 50,
  },
  {
    billId: 'TB-2025-0088', parcelId: 'LR-NIM-0088', address: 'Sanniquellie Vacant Lot', county: 'Nimba',
    owner: 'Nimba Land Holdings', assessedValue: 75000, propertyTypeKey: 'city_town_lots',
    propertyTypeLabel: 'City/Town Lots (within city/town limits)', taxRateDisplay: '3.5%', taxRate: 0.035,
    totalTax: 2625, taxCurrency: 'USD', dueDate: '2025-07-01', status: 'issued', paidAmount: 0, billDate: '2025-02-15',
    landCategory: 'undeveloped',
  },
];

const statusConfig = {
  issued: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Issued', icon: FileText },
  paid: { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'Paid', icon: CheckCircle },
  overdue: { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Overdue', icon: AlertTriangle },
  partial: { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: 'Partial', icon: Clock },
};

export default function TaxBillGenerationPanel() {
  const [selectedBill, setSelectedBill] = useState<TaxBill | null>(TAX_BILLS[0]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showRateTable, setShowRateTable] = useState(false);

  const filtered = TAX_BILLS.filter((b) => filterStatus === 'all' || b.status === filterStatus);
  const totalBilledUSD = TAX_BILLS.filter(b => b.taxCurrency === 'USD').reduce((s, b) => s + b.totalTax, 0);
  const totalCollectedUSD = TAX_BILLS.filter(b => b.taxCurrency === 'USD').reduce((s, b) => s + b.paidAmount, 0);
  const totalOutstandingUSD = totalBilledUSD - totalCollectedUSD;

  const formatUSD = (val: number) => `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatTax = (bill: TaxBill) =>
    bill.taxCurrency === 'LRD'
      ? `LRD ${bill.totalTax.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : formatUSD(bill.totalTax);

  return (
    <div className="space-y-4">
      {/* LRA Rate Reference Banner */}
      <div
        className="rounded-xl p-3 flex items-start gap-3 cursor-pointer"
        style={{ backgroundColor: 'rgba(212,160,23,0.07)', border: '1px solid rgba(212,160,23,0.25)' }}
        onClick={() => setShowRateTable(!showRateTable)}
      >
        <Info size={15} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} />
        <div className="flex-1">
          <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>
            LRA Official Real Property Tax Rates — Liberia Revenue Code
            <span className="ml-2 font-normal" style={{ color: 'var(--dark-muted)' }}>
              (click to {showRateTable ? 'hide' : 'view'} rate table)
            </span>
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            Formula: Declared Market Value × Tax Rate = Annual Tax Due · Taxes are due July 1st of the levy year
          </p>
        </div>
      </div>

      {/* Official LRA Rate Table */}
      {showRateTable && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
          <div className="grid grid-cols-2 divide-x" style={{ borderColor: 'var(--dark-border)' }}>
            {/* Developed Land */}
            <div>
              <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: 'rgba(96,165,250,0.12)', color: '#60a5fa', borderBottom: '1px solid var(--dark-border)' }}>
                Rates on Developed Land
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {[
                    { no: '1', label: 'Commercial Building', rate: '1.5%' },
                    { no: '2', label: 'Industrial Building', rate: '1.5%' },
                    { no: '3', label: 'Residential Building', rate: '0.25%' },
                    { no: '4', label: 'Farm in Urban Areas', rate: '0.33%' },
                    { no: '5', label: 'Farm outside Urban Areas', rate: '0.25%' },
                    { no: '6', label: 'Bldg/Improvement on Public Land', rate: '' },
                    { no: 'a', label: '  Commercial Building', rate: '1%' },
                    { no: 'b', label: '  Residential Building', rate: '0.14%' },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--dark-border)' }}>
                      <td className="px-3 py-2" style={{ color: 'var(--dark-muted)', width: 28 }}>{row.no}</td>
                      <td className="px-2 py-2" style={{ color: 'var(--dark-text)' }}>{row.label}</td>
                      <td className="px-3 py-2 font-bold font-mono-data text-right" style={{ color: 'var(--accent)', width: 56 }}>{row.rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Undeveloped Land */}
            <div>
              <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399', borderBottom: '1px solid var(--dark-border)' }}>
                Rates on Undeveloped Land
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {[
                    { no: '1', label: 'Unit within city or town limits:', rate: '' },
                    { no: 'a', label: '  City or town lots', rate: '3.5%' },
                    { no: 'b', label: '  Farmland', rate: '4%' },
                    { no: 'c', label: '  Acre or above (vacant land)', rate: '4.5%' },
                    { no: '2', label: 'Unit outside city or town limits:', rate: '' },
                    { no: 'a', label: '  All land (without building)', rate: 'LRD $5.00' },
                    { no: '', label: '', rate: 'per acre' },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--dark-border)' }}>
                      <td className="px-3 py-2" style={{ color: 'var(--dark-muted)', width: 28 }}>{row.no}</td>
                      <td className="px-2 py-2" style={{ color: 'var(--dark-text)' }}>{row.label}</td>
                      <td className="px-3 py-2 font-bold font-mono-data text-right" style={{ color: '#34d399', width: 72 }}>{row.rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="px-4 py-2 text-xs" style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-muted)', borderTop: '1px solid var(--dark-border)' }}>
            Source: Liberia Revenue Authority — Real Property Tax Schedule · Taxes due July 1st · Late penalty: 10% + 5% monthly (Section 51) · TIN required for payment
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Bill list */}
        <div className="lg:col-span-2 space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Total Billed (USD)', value: formatUSD(totalBilledUSD), color: 'var(--accent)' },
              { label: 'Collected', value: formatUSD(totalCollectedUSD), color: '#34d399' },
              { label: 'Outstanding', value: formatUSD(totalOutstandingUSD), color: '#f87171' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl p-3" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
                <p className="text-base font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'issued', 'paid', 'overdue', 'partial'].map((f) => (
              <button
                key={f}
                onClick={() => setFilterStatus(f)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-all capitalize"
                style={{
                  backgroundColor: filterStatus === f ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                  color: filterStatus === f ? 'var(--accent)' : 'var(--dark-muted)',
                  border: `1px solid ${filterStatus === f ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                }}
              >
                {f === 'all' ? 'All Bills' : f}
              </button>
            ))}
          </div>

          {/* Bills table */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                    {['Bill ID', 'Parcel', 'Owner', 'Property Type', 'LRA Rate', 'Tax Due', 'Status', ''].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((bill) => {
                    const cfg = statusConfig[bill.status];
                    const StatusIcon = cfg.icon;
                    const isSelected = selectedBill?.billId === bill.billId;
                    return (
                      <tr
                        key={bill.billId}
                        onClick={() => setSelectedBill(bill)}
                        className="cursor-pointer transition-colors hover:bg-white/3"
                        style={{
                          borderBottom: '1px solid var(--dark-border)',
                          backgroundColor: isSelected ? 'rgba(212,160,23,0.05)' : 'transparent',
                        }}
                      >
                        <td className="px-3 py-2.5 font-mono-data font-bold" style={{ color: 'var(--accent)' }}>{bill.billId}</td>
                        <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>{bill.parcelId}</td>
                        <td className="px-3 py-2.5 max-w-[100px] truncate" style={{ color: 'var(--dark-text)' }}>{bill.owner}</td>
                        <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>
                          <span className="block truncate max-w-[130px]">{bill.propertyTypeLabel}</span>
                          <span className="text-xs" style={{ color: bill.landCategory === 'developed' ? '#60a5fa' : '#34d399' }}>
                            {bill.landCategory === 'developed' ? 'Developed' : 'Undeveloped'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono-data font-bold" style={{ color: 'var(--accent)' }}>{bill.taxRateDisplay}</td>
                        <td className="px-3 py-2.5 font-mono-data font-semibold" style={{ color: 'var(--dark-text)' }}>{formatTax(bill)}</td>
                        <td className="px-3 py-2.5">
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full w-fit"
                            style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                            <StatusIcon size={10} />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <button className="p-1 rounded hover:bg-white/5" style={{ color: 'var(--dark-muted)' }}>
                            <Download size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right: Bill detail / calculator */}
        <div className="space-y-3">
          {selectedBill ? (
            <>
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Calculator size={16} style={{ color: 'var(--accent)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>LRA Tax Calculation</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs py-1.5 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                    <span style={{ color: 'var(--dark-muted)' }}>Property Type</span>
                    <span className="text-right max-w-[140px] text-xs" style={{ color: 'var(--dark-text)' }}>{selectedBill.propertyTypeLabel}</span>
                  </div>
                  <div className="flex justify-between text-xs py-1.5 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                    <span style={{ color: 'var(--dark-muted)' }}>Land Category</span>
                    <span style={{ color: selectedBill.landCategory === 'developed' ? '#60a5fa' : '#34d399' }}>
                      {selectedBill.landCategory === 'developed' ? 'Developed Land' : 'Undeveloped Land'}
                    </span>
                  </div>
                  {selectedBill.assessedValue > 0 && (
                    <div className="flex justify-between text-xs py-1.5 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                      <span style={{ color: 'var(--dark-muted)' }}>Declared Market Value</span>
                      <span style={{ color: 'var(--dark-text)' }}>{formatUSD(selectedBill.assessedValue)}</span>
                    </div>
                  )}
                  {selectedBill.areaAcres && (
                    <div className="flex justify-between text-xs py-1.5 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                      <span style={{ color: 'var(--dark-muted)' }}>Land Area</span>
                      <span style={{ color: 'var(--dark-text)' }}>{selectedBill.areaAcres} acres</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs py-1.5 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                    <span style={{ color: 'var(--dark-muted)' }}>LRA Tax Rate</span>
                    <span className="font-bold" style={{ color: 'var(--accent)' }}>{selectedBill.taxRateDisplay}</span>
                  </div>
                  {/* Formula */}
                  <div className="rounded-lg p-2 mt-1" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>LRA Formula</p>
                    {selectedBill.taxRate !== null ? (
                      <p className="text-xs font-mono-data" style={{ color: 'var(--dark-text)' }}>
                        {selectedBill.assessedValue > 0 ? `$${selectedBill.assessedValue.toLocaleString()}` : '—'} × {selectedBill.taxRateDisplay} = <span style={{ color: 'var(--accent)' }}>{formatTax(selectedBill)}</span>
                      </p>
                    ) : (
                      <p className="text-xs font-mono-data" style={{ color: 'var(--dark-text)' }}>
                        {selectedBill.areaAcres} acres × LRD $5.00 = <span style={{ color: 'var(--accent)' }}>LRD {selectedBill.totalTax.toLocaleString()}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex justify-between text-sm font-bold pt-1">
                    <span style={{ color: 'var(--dark-text)' }}>Total Tax Due</span>
                    <span style={{ color: 'var(--accent)' }}>{formatTax(selectedBill)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-text)' }}>Payment Status</p>
                <div className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--dark-muted)' }}>Collected</span>
                    <span style={{ color: '#34d399' }}>{Math.round(selectedBill.paidAmount / selectedBill.totalTax * 100)}%</span>
                  </div>
                  <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <div className="h-2 rounded-full" style={{
                      width: `${Math.min(100, selectedBill.paidAmount / selectedBill.totalTax * 100)}%`,
                      backgroundColor: '#34d399',
                    }} />
                  </div>
                </div>
                <div className="flex justify-between text-xs mb-1">
                  <span style={{ color: 'var(--dark-muted)' }}>Due Date</span>
                  <span style={{ color: selectedBill.status === 'overdue' ? '#f87171' : 'var(--dark-text)' }}>{selectedBill.dueDate}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--dark-muted)' }}>Bill Date</span>
                  <span style={{ color: 'var(--dark-muted)' }}>{selectedBill.billDate}</span>
                </div>
              </div>

              {/* LRA Payment Methods */}
              <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.2)' }}>
                <p className="text-xs font-semibold mb-1.5" style={{ color: '#34d399' }}>LRA Payment Channels</p>
                <div className="space-y-1 text-xs" style={{ color: 'var(--dark-muted)' }}>
                  <p>📱 MTN Mobile Money: *156#</p>
                  <p>📱 Orange Money: *144#</p>
                  <p>🏦 Commercial bank (manager&apos;s check for &gt;$250)</p>
                  <p>🏢 LRA Head Office or nearest tax office (&lt;$250 cash)</p>
                  <p>💻 E-filing: eservices.lra.gov.lr</p>
                  <p>📧 Email: info@lra.gov.lr</p>
                </div>
              </div>

              <button className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}>
                <Send size={14} />
                Send Bill to Owner
              </button>
            </>
          ) : (
            <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <FileText size={32} className="mx-auto mb-2" style={{ color: 'var(--dark-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>Select a bill to view calculation details</p>
            </div>
          )}
        </div>
      </div>

      {/* LRA Compliance Note */}
      <div className="rounded-xl p-3 flex items-start gap-2" style={{ backgroundColor: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.2)' }}>
        <AlertTriangle size={14} style={{ color: '#f87171', flexShrink: 0, marginTop: 1 }} />
        <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
          <span className="font-semibold" style={{ color: '#f87171' }}>LRA Compliance: </span>
          Property owners must obtain a TIN before payment. Failure to file within 30 days of acquisition: 10% penalty + 5% monthly (Section 51). Taxes are levied July 1st of each year. Under-declaration is subject to additional assessment. Return forms available at lra.gov.lr or any LRA office.
        </p>
      </div>
    </div>
  );
}
